import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useMutation } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined"
import ReviewsIcon from "@mui/icons-material/Reviews"
import { Card, CircularProgress, Grid, Link } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Button, InputText, NotificationUtils, Select, Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import _, { isEmpty, truncate } from "lodash"
import type { MRT_ColumnDef, MRT_Row, MRT_TableInstance } from "material-react-table"
import moment from "moment"

import { FeedbackSidebar } from "../../features/Decisions/components/Decisions/components/FeedbackSidebar"
import { ActualDecisionSelector } from "../../features/Decisions/components/Elements/ActualDecisionSelector"
import { ReviewActions } from "../../features/Decisions/components/Elements/ReviewActions"
import { DecisionsContext } from "../../features/Decisions/context/Context"
import { getTheme } from "../../hooks/UseTheme"
import { KonanAPI } from "../../services/KonanAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import type { TagColor } from "../../types/custom/projects"
import type { ActualDecisionCreateRequest } from "../../types/generated/api/ActualDecisionCreateRequest"
import type { ActualDecisionCreateResponse } from "../../types/generated/api/ActualDecisionCreateResponse"
import { ExportJobCreateRequest } from "../../types/generated/api/ExportJobCreateRequest"
import type { ListPredictionsOutput } from "../../types/generated/api/ListPredictionsOutput"
import { TableConfigurationRequest } from "../../types/generated/api/TableConfigurationRequest"
import type { StandardErrorType } from "../../utils/ErrorHandling"
import { convertNestedObjectsToFeatureStrings } from "../../utils/modelDetailsHelpers"
import { JsonPopper } from "../data-display/JsonPopper"
import { ActualDecisionsDialog } from "../dialogs/ActualDecisionsDialog"
import { ExportDataDialog } from "../dialogs/ExportDataDialog"
import { SelectWithSearch } from "../form/SelectWithSearch/SelectWithSearch"
import { KonanCopyToClipboard } from "../utility/KonanCopyToClipboard"
import { BaseTableContainer } from "./BaseTableContainer"
import { TAG_VARIANTS } from "./CONSTANTS"
import { CustomTableHeaders, CustomTableHeadersWithInternalTooltip } from "./CustomTableHeader"

import styles from "./ProjectDecisions.module.scss"

let tagVariants = [...TAG_VARIANTS]

const tagVariantHash: Record<string, string> = {}

let isInitialRender = true

export function TableLoadingState(): React.ReactElement {
  const { t } = useTranslation("decisions", {
    keyPrefix: "projectDecisions",
  })

  return (
    <Card className="card-box-shadow" style={{ height: "300px", display: "flex" }}>
      <Grid container className={styles.emptyStateRoot}>
        <Grid item xs={12} className={styles.emptyStateItem}>
          <CircularProgress size={20} color="primary" />
        </Grid>
        <Grid item xs={12} className={styles.emptyStateItem}>
          <Typography variant={"p"}>{t("fetchingData")}</Typography>
        </Grid>
      </Grid>
    </Card>
  )
}

export function ProjectDecisionsTable(): React.ReactElement {
  const {
    data,
    isFetching,
    isLoading,
    pagination,
    setPagination,
    filterObject,
    setFilterObject,
    reviewLabel,
    setReviewLabel,
    jointOutputFilter,
    setJointOutputFilter,
    reasonFilter,
    setReasonFilter,
    feedbackFilter,
    setFeedbackFilter,
    fetchNextLabelsPage,
    isFetchingLabelsNextPage,
    hasNextLabelsPage,
    labels,
    setLabelsSearchValue,
    setSelectedRow,
  } = useContext(DecisionsContext)

  const { id: projectId } = useParams<{ id: string }>()

  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const tableRef = useRef<MRT_TableInstance>(null)

  const theme = getTheme()

  const { t } = useTranslation("decisions", {
    keyPrefix: "projectDecisions",
  })

  const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false)
  const [isActualDecisionsDialogOpen, setIsActualDecisionsDialogOpen] = useState(false)
  const [isFeedbackSideBarOpen, setIsFeedbackSideBarOpen] = useState(false)

  const [isFilePending, setIsFilePending] = useState(false)

  const [selectedRows, setSelectedRows] = useState({})

  const [bulkActualDecisionInitialText, setBulkActualDecisionInitialText] = useState(t("status.notProvided"))

  const clearFilter = useCallback(
    (item: string, type: string): void => {
      const tempObject = filterObject
      // eslint-disable-next-line
      // @ts-ignore

      switch (type) {
        case "features":
          // eslint-disable-next-line
          // @ts-ignore
          delete tempObject[`${type}__${item.split(".").join("__")}`]

          setFilterObject({
            ...tempObject,
          })
          break
        case "joint_output":
          setJointOutputFilter(undefined)
          break
        case "reason":
          setReasonFilter(undefined)
          break
        case "feedback":
          setFeedbackFilter(undefined)
          break
        default:
          break
      }

      setPagination?.({
        pageIndex: 0,
        pageSize: 10,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterObject, jointOutputFilter, reasonFilter, feedbackFilter],
  )

  const handleFilterChange = useCallback(
    (value: string | number, title: string, type: string): void => {
      const tempObject = filterObject

      if (!value) {
        clearFilter(title, type)
        return
      }

      switch (type) {
        case "features":
          // eslint-disable-next-line
          // @ts-ignore
          tempObject[`${type}__${title.split(".").join("__")}`] = value
          setFilterObject({
            ...tempObject,
          })
          break
        case "joint_output":
          setJointOutputFilter(value as string)
          break
        case "reason":
          setReasonFilter(value as string)
          break
        case "feedback":
          setFeedbackFilter(value as string)
          break
        default:
          break
      }

      setPagination?.({
        pageIndex: 0,
        pageSize: 10,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterObject, jointOutputFilter, reasonFilter, feedbackFilter],
  )

  const assignTagVariantToReasonValue = (reason: string): void => {
    // refilling color array if we run out of variants
    // TODO:: should find a dynamic way to fill tag color later
    if (tagVariants.length === 0) tagVariants = [...TAG_VARIANTS]
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    tagVariantHash[reason] = tagVariants[0]

    tagVariants.splice(0, 1)
  }

  const decisionReason = (rowData: ListPredictionsOutput): { text: string; tooltip: string; type: string } => {
    const tooltip = rowData.workflow_output?.description
    let text = ""
    let type = currentProject?.category === "workflow" ? "__" : t("aiEngine")

    if (rowData.workflow_output) {
      // getting last node in the satisfied_flow_path
      // to show it's data in the reason column since it's the last node the prediction ran into
      let lastNode = rowData.workflow_output?.satisfied_flow_path?.slice(-1)[0]
      if (lastNode?.type !== "project") {
        if (lastNode?.type === "loop-start") {
          const lastIteration = lastNode.iterations[Object.keys(lastNode.iterations).length]
          lastNode = lastIteration[lastIteration.length - 1]
        }

        // Adding __ as a fallback for when there isn't any nodes in the satisfied_flow_path to parse the data from
        text = lastNode ? `${lastNode?.type?.charAt(0).toUpperCase() + lastNode?.type?.slice(1)}` : "__"
      }

      type = lastNode?.type
      if (rowData.workflow_output.reason) type += `: ${rowData.workflow_output.reason}`
    }
    return { text: text, type: type, tooltip: tooltip }
  }

  const debouncedFilter = useMemo(() => {
    return _.debounce(handleFilterChange, 1000)
  }, [handleFilterChange])

  // get table columns from response
  const tableColumns = useMemo((): { title: string; field: string; type: string }[] => {
    const columnSet = new Set()

    // loop over the list to collect all possible columns
    if (data?.results?.outputs && data.results.outputs.length > 0) {
      for (let i = 0; i < data.results.outputs.length; i++) {
        if (data?.results.outputs[i]?.features_json) {
          for (const item in convertNestedObjectsToFeatureStrings({ obj: data?.results.outputs[i]?.features_json })) {
            const adjustedItem = {
              item: item,
              type: "features",
            }
            const stringifiedItem = JSON.stringify(adjustedItem)
            !columnSet.has(stringifiedItem) && columnSet.add(stringifiedItem)
          }

          // adding calculated features to table columns
          for (const item in convertNestedObjectsToFeatureStrings({
            obj: data?.results.outputs[i]?.workflow_output?.calculated_features,
            exclusionList: data.results.workflow_response_filters,
          })) {
            const adjustedItem = {
              item: item,
              type: "calculated_features",
            }
            const stringifiedItem = JSON.stringify(adjustedItem)
            !columnSet.has(stringifiedItem) && columnSet.add(stringifiedItem)
          }

          // adding calculated features to table columns
          for (const item in convertNestedObjectsToFeatureStrings({
            obj: data?.results.outputs[i]?.be_output?.output,
            exclusionList: data.results.workflow_response_filters,
          })) {
            const adjustedItem = {
              item: item,
              type: t("modelOutput"),
            }
            const stringifiedItem = JSON.stringify(adjustedItem)
            !columnSet.has(stringifiedItem) && columnSet.add(stringifiedItem)
          }
        }
      }
    }

    return data?.results && data?.results?.outputs.length > 0
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Array.from(columnSet.keys()).map((column: any) => {
          const item = JSON.parse(column)
          return {
            title: item.item,
            field: item.item,
            type: item.type,
            align: "center",
          }
        })
      : []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.results])

  // polling to check if the file processing has ended
  const { data: filesDetailsRes } = useInfiniteQuery({
    queryKey: ["file-creation-jobs"],
    queryFn: ({ pageParam = 1 }) =>
      KonanAPI.fetchActualDecisionsFileCreationJobs({
        project_uuid: projectId as string,
        page: pageParam,
        page_size: 10,
      }),
    onSuccess: (res) => {
      const data = res.pages[0]?.data.results?.[0]

      if (data?.status?.toLowerCase() === "succeeded" && !isInitialRender) {
        NotificationUtils.toast(t("notifications.fileProcessed"), { severity: "success" })

        queryClient.invalidateQueries("project-predictions")
      }

      if (data?.status?.toLowerCase() === "failed" && !isInitialRender) {
        NotificationUtils.toast(
          t("notifications.fileProcessingFailed", {
            successfulCount: data.successful_count,
            failedCount: data.failed_count,
          }),
          { severity: "error" },
        )
      }

      if (data?.status?.toLowerCase() !== "pending" && data?.status?.toLowerCase() !== "running") {
        setIsFilePending(false)
      }
    },
    onSettled: () => {
      isInitialRender = false
    },
    refetchInterval: (res) => {
      const status = res?.pages[0]?.data.results?.[0]?.status?.toLowerCase()

      if (status === "succeeded" || status === "failed") return false
      return 3000
    },
    refetchIntervalInBackground: true,
    enabled: isFilePending || isInitialRender,
  })

  const cancelActualDecisionsFileMutation = useMutation(KonanAPI.cancelActualDecisionsFile, {
    onSuccess: () => {
      NotificationUtils.toast(t("notifications.fileCancelled"), { severity: "success" })
      setIsActualDecisionsDialogOpen(false)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries("file-creation-jobs")
      setIsFilePending(false)
    },
  })

  const createActualDecisionMutation = useMutation<
    AxiosResponse<ActualDecisionCreateResponse>,
    AxiosError<StandardErrorType>,
    ActualDecisionCreateRequest
  >(KonanAPI.createActualDecisions, {
    onSuccess() {
      NotificationUtils.toast(t("notifications.actualDecisionUpdated"), { severity: "success" })
      queryClient.invalidateQueries("project-predictions")
      setBulkActualDecisionInitialText(t("status.notProvided"))
    },
  })

  const clearAllFilters =
    Object.keys(filterObject).length > 0 || !!jointOutputFilter || !!reasonFilter || !!feedbackFilter
      ? () => {
          setFilterObject({})
          setJointOutputFilter(undefined)
          setReasonFilter(undefined)
          setFeedbackFilter(undefined)
        }
      : undefined

  const adjustedData = useMemo(
    (): ListPredictionsOutput[] =>
      data?.results && data?.results.outputs.length > 0
        ? data?.results.outputs.map((item: ListPredictionsOutput) =>
            // TODO:: refactor to avoid any overlap between preset object elements names and schema/cfn names
            ({
              ...item,
              ...item.features_json,
              ...item.workflow_output?.calculated_features,
              ...item.be_output?.output,
              backend_review: item.review,
              labels,
            }),
          )
        : [],
    [data, labels],
  )

  const { ref: menuItemRef } = useInView({
    onChange(inView) {
      inView && hasNextLabelsPage && fetchNextLabelsPage()
    },
  })

  // this function handles both (background color and text color) in decision column,
  // inside classification metrics screen
  function getDecisionColumn(
    rowData: ListPredictionsOutput,
  ): { backgroundColor: string; text: string; color: "base" | "negative" | "positive" } | undefined {
    const theme = getTheme()
    const result: { backgroundColor: string; text: string; color: "base" | "negative" | "positive" } = {
      backgroundColor: "__",
      text: "__",
      color: "base",
    }

    if (rowData.feedback === null) {
      result.backgroundColor = ""
      result.color = "base"
    } else {
      if (rowData.feedback && rowData.joint_output) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (rowData.feedback === rowData.joint_output) {
          result.backgroundColor = theme.palette.green.background[2]
          result.color = "positive"
        } else {
          result.backgroundColor = theme.palette.red.background[2]
          result.color = "negative"
        }
      } else if (rowData.feedback && rowData.be_output?.target_label) {
        if (rowData.feedback === rowData.be_output?.target_label) {
          result.backgroundColor = theme.palette.green.background[2]
          result.color = "positive"
        } else {
          result.backgroundColor = theme.palette.red.background[2]
          result.color = "negative"
        }
      }
    }

    if (rowData.joint_output != undefined) {
      result.text = rowData.joint_output
    } else if (rowData.be_output?.target_label != undefined) {
      if (rowData.be_output?.target_label === "uncovered") {
        result.text = t("uncovered")
      } else if (rowData.be_output?.target_label === "invalid") {
        result.text = t("invalid")
      } else {
        result.text = rowData.be_output?.target_label
      }
    } else {
      result.text = "__"
    }

    return result
  }

  const adjustedActualDecisionsOptions = useMemo(() => {
    const adjustedLabels = labels?.map((label, i) => ({
      text: label.name,
      value: label.uuid,
      item: (
        <Typography variant="a" variantColor={i === 0 ? 2 : 1}>
          {label.name}
        </Typography>
      ),
      disabled: false,
      listItemStyles: i === 0 ? { marginBottom: "0px" } : undefined,
    }))

    if (labels?.[0]?.name === t("status.notProvided") && labels?.length > 1) {
      adjustedLabels?.splice(1, 0, {
        text: "",
        value: "",
        item: <div className={styles.separator}></div>,
        disabled: true,
        listItemStyles: undefined,
      })
    }
    return adjustedLabels
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels])

  const rowIndexColumn: MRT_ColumnDef<ListPredictionsOutput> = {
    id: "row_index",
    accessorKey: "row_index",
    header: "idx",
    size: 50,
    Cell: ({ row, table }) => {
      const { pageIndex, pageSize } = table.getState().pagination
      return pageIndex * pageSize + row.index + 1
    },
  }

  const adjustedColumns = useMemo(() => {
    return [
      rowIndexColumn,
      {
        header: t("requestDate"),
        accessorKey: "created_at",
        Cell: ({ row }: { row: MRT_Row<ListPredictionsOutput> }) => {
          return (
            <Typography variant={"p"}>
              {moment(row.original?.created_at).utc().format("MMM DD YYYY, H:mm:ss")}
            </Typography>
          )
        },
      },
      ...(currentProject?.category === "workflow"
        ? [
            {
              header: t("reviewed"),
              accessorKey: "Reviewed",
              Filter: () => (
                <Select
                  id="reviewed-filter"
                  placeholder={t("all")}
                  value={reviewLabel}
                  optionsWithValues={[
                    { label: t("all"), value: "" },
                    { label: t("reviewed"), value: "REVIEWED" },
                    { label: t("revoked"), value: "REVOKED" },
                  ]}
                  handleChange={(e) => {
                    setReviewLabel(e.target.value === "" ? undefined : (e.target.value as "REVIEWED" | "REVOKED"))
                  }}
                  fullWidth
                />
              ),
              Cell: ({ row }: { row: MRT_Row<ListPredictionsOutput> }) => {
                return (
                  <div onClick={(e) => e.stopPropagation()}>
                    <ReviewActions original={row.original} />
                  </div>
                )
              },
            },
          ]
        : []),
      {
        header: "ID",
        accessorKey: "features_uuid",
        Cell: ({ row }: { row: MRT_Row<ListPredictionsOutput> }) => {
          return (
            <Typography variant="p" noWrap>
              <KonanCopyToClipboard text={`${row.original?.uuid}`} />
              {truncate(row.original?.uuid, { length: 10 })}
            </Typography>
          )
        },
      },
      ...(currentProject?.category === "workflow"
        ? [
            {
              header: "Workflow",
              accessorKey: "workflow_name",
              Cell: ({ row }: { row: MRT_Row<ListPredictionsOutput> }) => {
                return (
                  <Tooltip title={row.original?.workflow_output?.workflow?.version ?? "__"}>
                    <Typography variant="p" noWrap>
                      {row.original?.workflow_output?.workflow?.name ?? "__"}
                    </Typography>
                  </Tooltip>
                )
              },
            },
          ]
        : []),
      {
        header: t("decision"),
        Header: (
          <CustomTableHeaders
            title={t("decision")}
            tooltipText={
              <React.Fragment>
                {t("tooltip.decision.description")}
                <br />
                {t("tooltip.decision.rulesOverride")}
                <br />
                {t("tooltip.decision.willBeColored")}
                <ul>
                  <li>
                    <strong style={{ color: theme.palette.green.text[2] }}>GREEN</strong>{" "}
                    {t("tooltip.decision.greenForCorrect")}
                  </li>
                  <li>
                    <strong style={{ color: theme.palette.red.text[2] }}>RED</strong>{" "}
                    {t("tooltip.decision.redForIncorrect")}
                  </li>
                  <li>
                    <strong>UNCOLORED</strong> {t("tooltip.decision.uncoloredForPending")}
                  </li>
                </ul>
              </React.Fragment>
            }
          />
        ),
        accessorKey: "joint_output",
        muiTableBodyCellProps: ({ row }: { row: MRT_Row<ListPredictionsOutput> }) => {
          return { sx: { background: getDecisionColumn(row.original)?.backgroundColor } }
        },
        Filter: () => {
          const [value, setValue] = useState<string | undefined>(jointOutputFilter ?? undefined)

          return (
            <InputText
              id={"joint_output"}
              key={"joint_output"}
              hideDescription
              placeholder={t("filter")}
              fullWidth
              value={value}
              handleChange={(e) => {
                setValue(e.target.value?.toString())
                debouncedFilter(e.target.value, t("decision"), "joint_output")
              }}
              endAdornment={
                !["", null, undefined].includes(value) ? (
                  <CloseIcon
                    fontSize="small"
                    onClick={() => {
                      setValue(undefined)
                      clearFilter(t("decision"), "joint_output")
                      ;(document.getElementById("joint_output") as HTMLInputElement).value = ""
                    }}
                  />
                ) : undefined
              }
            />
          )
        },
        Cell: ({ row }: { row: MRT_Row<ListPredictionsOutput> }) => {
          const decision = getDecisionColumn(row.original)
          return (
            <Typography variant={"p"} color={decision?.color} variantColor={decision?.color === "base" ? 1 : 2}>
              {decision?.text}
            </Typography>
          )
        },
      },
      {
        header: t("reason"),
        Header: <CustomTableHeaders title={t("reason")} tooltipText={t("tooltip.reason")} />,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessorFn: (row: any) => {
          return decisionReason(row).type
        },
        id: "reason_row",
        accessorKey: "reason_row",
        Cell: ({ row }: { row: MRT_Row<ListPredictionsOutput> }) => {
          const reason = decisionReason(row.original)
          // checking if reason is not in the tag variant hash to assign a tag to it
          // else skip and render the tag directly
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          !tagVariantHash[reason.text] && assignTagVariantToReasonValue(reason.text)
          return (
            <Grid container wrap="nowrap" display={"flex"}>
              {reason.type === "__" ? (
                <Typography variant={"p"}>{reason.type}</Typography>
              ) : (
                <Tooltip title={reason.tooltip}>
                  <Tag color={(tagVariantHash[reason.text] as TagColor) ?? "green"} style={{ whiteSpace: "nowrap" }}>
                    {reason.type ?? t("reasonNotSpecified")}
                  </Tag>
                </Tooltip>
              )}
            </Grid>
          )
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Filter: (filterProps: any) => {
          const [value, setValue] = useState<string | undefined>(reasonFilter ?? undefined)

          const filterValue = filterProps.column.getFilterValue() as string

          return (
            <InputText
              id={"reason"}
              key={"reason"}
              hideDescription
              placeholder={t("filter")}
              fullWidth
              value={value}
              handleChange={(e) => {
                if (currentProject?.category === "workflow") {
                  setValue(e.target.value?.toString())
                  debouncedFilter(e.target.value, t("reason"), "reason")
                } else filterProps.column.setFilterValue(e.target.value)
              }}
              endAdornment={
                !["", null, undefined].includes(value || filterValue) ? (
                  <CloseIcon
                    fontSize="small"
                    onClick={() => {
                      if (currentProject?.category === "workflow") {
                        setValue(undefined)
                        clearFilter(t("reason"), "reason")
                      } else filterProps.column.setFilterValue("")
                      ;(document.getElementById("reason") as HTMLInputElement).value = ""
                    }}
                  />
                ) : undefined
              }
            />
          )
        },
      },
      {
        header: t("actualDecision"),
        Header: <CustomTableHeaders title={t("actualDecision")} tooltipText={t("tooltip.actualDecision")} />,
        accessorKey: "actual_decision",
        Cell: ({ row }: { row: MRT_Row<ListPredictionsOutput> }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <ActualDecisionSelector
              projectId={projectId}
              current={row.original}
              labels={labels}
              hasNextLabelsPage={hasNextLabelsPage}
              fetchNextLabelsPage={fetchNextLabelsPage}
              setLabelsSearchValue={setLabelsSearchValue}
            />
          </div>
        ),
      },
      {
        header: t("feedback"),
        Header: <CustomTableHeaders title={t("feedback")} tooltipText={t("tooltip.feedback")} />,
        accessorKey: "feedback_row",
        Cell: ({ row }: { row: MRT_Row<ListPredictionsOutput> }) => {
          const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

          return ["object"].includes(typeof row?.original?.feedback) && !!row?.original?.feedback ? (
            <Fragment>
              {Boolean(anchorEl) && (
                <JsonPopper
                  id={row.original.uuid}
                  isOpen={Boolean(anchorEl)}
                  onClose={function (): void {
                    setAnchorEl(null)
                  }}
                  anchorEl={anchorEl}
                  content={row.original.feedback}
                />
              )}

              {/* TODO:: replace with SUI anchor component in the revamp
                  leaving the styles inline to remember to remove them when revamping */}

              <Link
                onClick={(e) => {
                  setAnchorEl(e.target as HTMLButtonElement | null)
                  e.stopPropagation()
                }}
                style={{
                  fontFamily: '"inter",sans-serif',
                  fontWeight: "400",
                  fontSize: "14px",
                  lineHeight: "20px",
                  cursor: "pointer",
                }}
              >
                {t("viewFeedback")}
              </Link>
            </Fragment>
          ) : (
            <Typography variant={"p"}>{(row?.original?.feedback ?? "__") as string}</Typography>
          )
        },
        Filter: () => {
          const [value, setValue] = useState<string | undefined>(feedbackFilter ?? undefined)

          return (
            <InputText
              id={"feedback"}
              key={"feedback"}
              hideDescription
              placeholder={t("filter")}
              fullWidth
              value={value}
              handleChange={(e) => {
                setValue(e.target.value?.toString())
                debouncedFilter(e.target.value, t("feedback"), "feedback")
              }}
              endAdornment={
                !["", null, undefined].includes(value) ? (
                  <CloseIcon
                    fontSize="small"
                    onClick={() => {
                      setValue(undefined)
                      clearFilter(t("feedback"), "feedback")
                      ;(document.getElementById("feedback") as HTMLInputElement).value = ""
                    }}
                  />
                ) : undefined
              }
            />
          )
        },
      },
      ...tableColumns
        // TODO:: Refactor later
        .toSorted((a, _b) => (a.title === "KONAN_TAGS" ? -1 : 1))
        .map((item): MRT_ColumnDef<ListPredictionsOutput> => {
          const parsedTitle = item.title.split(".")
          const type = item.type

          return {
            ...item,
            accessorKey: `${item.type}-${item.title}`,
            header: item.title,
            Header: (
              <CustomTableHeadersWithInternalTooltip
                title={parsedTitle[parsedTitle.length - 1] ?? ""}
                tooltipText={
                  <Fragment>
                    {t("source")} {item.type}
                    <br />
                    {t("name")} {item.title}
                  </Fragment>
                }
              />
            ),
            Filter: () => {
              const [value, setValue] = useState<string | number | undefined | unknown>(
                filterObject[`${item.type}__${item.title}`] ?? undefined,
              )

              return (
                <InputText
                  id={`${type}-${item.title}`}
                  key={`${type}-${item.title}`}
                  hideDescription
                  placeholder={t("filter")}
                  fullWidth
                  value={value as string | number | undefined}
                  handleChange={(e) => {
                    const value = e.target.value?.toString()

                    setValue(value)
                    debouncedFilter(
                      item.title === "KONAN_TAGS" || value.includes(",")
                        ? (
                            value.split(",").map((item) => (typeof item === "string" ? item.trim() : item)) ?? value
                          ).join(",")
                        : value,
                      item.title,
                      type,
                    )
                  }}
                  endAdornment={
                    !!value ? (
                      <CloseIcon
                        fontSize="small"
                        onClick={() => {
                          setValue("")
                          clearFilter(item.title, type)
                          ;(document.getElementById(`${type}-${item.title}`) as HTMLInputElement).value = ""
                        }}
                      />
                    ) : undefined
                  }
                />
              )
            },
            Cell: ({ row }) => {
              const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

              const value = _.get(row.original, item.title)

              return Boolean(value) && (typeof value === "object" || (Array.isArray(value) && value.length > 0)) ? (
                <Fragment>
                  {Boolean(anchorEl) && (
                    <JsonPopper
                      id={row.original.uuid}
                      isOpen={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(null)}
                      anchorEl={anchorEl}
                      content={value}
                    />
                  )}

                  {/* TODO:: replace with SUI anchor component in the revamp
                    leaving the styles inline to remember to remove them when revamping */}
                  <Link
                    onClick={(e) => {
                      setAnchorEl(e.target as HTMLButtonElement | null)
                      e.stopPropagation()
                    }}
                    style={{
                      fontFamily: '"inter",sans-serif',
                      fontWeight: "400",
                      fontSize: "14px",
                      lineHeight: "20px",
                      cursor: "pointer",
                    }}
                  >
                    {t("view")}
                  </Link>
                </Fragment>
              ) : typeof value === "boolean" ? (
                value ? (
                  <CheckIcon style={{ fontSize: "1.1rem" }} />
                ) : (
                  <CloseIcon style={{ fontSize: "1.1rem" }} />
                )
              ) : (
                <Typography variant={"p"}>
                  {Array.isArray(value) && value.length === 0 ? "__" : ((value ?? "__") as string)}
                </Typography>
              )
            },
          }
        }),
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableColumns, adjustedActualDecisionsOptions, isFetchingLabelsNextPage, createActualDecisionMutation.isLoading])

  // When the component unmounts, the search request could be still in progress.
  //  In this case, an error will occur when the characters state is set.
  useEffect(() => {
    return () => {
      debouncedFilter.cancel()
    }
  }, [debouncedFilter])

  const fileStatus = filesDetailsRes?.pages[0]?.data.results?.[0]?.status?.toLowerCase()

  const isActualDecisionFilePending = fileStatus === "pending" || fileStatus === "running"

  return (
    <Fragment>
      {isExportDialogOpen && (
        <ExportDataDialog
          onClose={() => setIsExportDialogOpen(false)}
          jobType={ExportJobCreateRequest.type.DEPLOYMENT_PREDICTIONS}
        />
      )}

      {isActualDecisionsDialogOpen && (
        <ActualDecisionsDialog
          onClose={() => setIsActualDecisionsDialogOpen(false)}
          setIsFilePending={setIsFilePending}
        />
      )}

      {isFeedbackSideBarOpen && <FeedbackSidebar onClose={() => setIsFeedbackSideBarOpen(false)} />}

      {!isLoading ? (
        <BaseTableContainer
          onRowClick={(row: MRT_Row<ListPredictionsOutput>) => {
            setSelectedRow(row.original)
          }}
          id="project-decisions-table"
          title={t("projectDecisions")}
          tooltip={t("tooltip.projectDecisions")}
          columns={adjustedColumns as MRT_ColumnDef[]}
          pinColumns={["row_index", "features_uuid"]}
          data={adjustedData}
          isLoading={isFetching}
          source={TableConfigurationRequest.table.LIVE_DECISIONS}
          manualPagination
          rowCount={data?.count}
          setPagination={setPagination}
          pagination={pagination}
          hiddenColumns={data?.results?.workflow_response_filters ?? []}
          clearAllFiltersFn={clearAllFilters}
          onExportClick={() => setIsExportDialogOpen(true)}
          tableRef={tableRef}
          getRowId={(originalRow: { uuid: string }) => originalRow.uuid}
          onRowSelectionChange={setSelectedRows}
          rowSelection={selectedRows}
          enableSelectAll={true}
          actions={[
            <Button
              isLoading={cancelActualDecisionsFileMutation.isLoading}
              disabled={cancelActualDecisionsFileMutation.isLoading}
              startIcon={
                !isActualDecisionFilePending ? (
                  <FolderOutlinedIcon style={{ width: "20px", height: "20px" }} />
                ) : (
                  <CircularProgress style={{ width: "20px", height: "20px" }} />
                )
              }
              variant={!isActualDecisionFilePending ? "secondary" : "dangerous"}
              onClick={() => {
                if (isActualDecisionFilePending) {
                  cancelActualDecisionsFileMutation.mutate({
                    project_uuid: projectId as string,
                    file_creation_uuid: filesDetailsRes?.pages[0]?.data.results?.[0]?.uuid as string,
                  })
                } else {
                  setIsActualDecisionsDialogOpen(true)
                }
              }}
            >
              {isActualDecisionFilePending ? t("cancelPendingFile") : t("actualDecision")}
            </Button>,
            <Button
              startIcon={<ReviewsIcon style={{ width: "20px", height: "20px" }} />}
              variant={"secondary"}
              onClick={() => setIsFeedbackSideBarOpen(true)}
            >
              {t("sendFeedback")}
            </Button>,
            !isEmpty(selectedRows) ? (
              <Grid width={160}>
                <SelectWithSearch
                  key={Object.keys(selectedRows).length}
                  inputValue={bulkActualDecisionInitialText}
                  onSelectMenuItem={({ value, text }) => {
                    setBulkActualDecisionInitialText(text)

                    createActualDecisionMutation.mutate({
                      project_uuid: projectId as string,
                      decisions: Object.keys(selectedRows).map((id) => ({
                        prediction: id,
                        label: value === "" ? null : value,
                      })),
                    })
                  }}
                  optionsList={adjustedActualDecisionsOptions || []}
                  menuTriggerProps={{
                    disabled: createActualDecisionMutation.isLoading,
                    fullWidth: true,
                    hideDescription: true,
                  }}
                  isOptionsLoading={isFetchingLabelsNextPage}
                  searchInputChangeHandler={(e: InputChangeEvent) => {
                    setLabelsSearchValue(e.target.value.toString())
                  }}
                  onMenuClose={() => setLabelsSearchValue(undefined)}
                  lastMenuItemRef={menuItemRef}
                />
              </Grid>
            ) : undefined,
          ]}
          enableRowSelection
        />
      ) : (
        <TableLoadingState />
      )}
    </Fragment>
  )
}
